import type { IdentityLookupRow } from '../api/identityVerificationDashboardApi';
import { identitySessionHasPersistedEvidence } from './applicationDetailTabStatus';
import {
  classifyMitekAuthenticity,
  mitekAuthenticityOperatorCopy,
} from './mitekAuthenticitySignal';

export type IdentitySummaryTone = 'success' | 'warning' | 'error' | 'info';

export type IdentitySessionField = {
  label: string;
  value: string;
};

export type IdentitySessionSummary = {
  tone: IdentitySummaryTone;
  /** Short admin headline, e.g. "Passed". */
  title: string;
  /** One or two plain sentences — no timeline. */
  body: string;
  /** Divider KV rows for the attempt card (Document / Looks like / Checked). */
  fields: IdentitySessionField[];
  /** Caption lines derived from fields (legacy / tests). */
  details: string[];
};

function customerDisplayName(customer: {
  first_name: string | null;
  last_name: string | null;
  ccid: string;
} | null | undefined): string | null {
  if (!customer) return null;
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim();
  return name || customer.ccid;
}

function readOcrString(
  ocr: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  if (!ocr) return null;
  const value = ocr[key] ?? ocr[key === 'document_type' ? 'documentType' : key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function documentValue(row: IdentityLookupRow): string | null {
  const ocr = asRecord(row.ocr_data);
  const rowRecord = row as IdentityLookupRow & Record<string, unknown>;
  const preOcr = asRecord(rowRecord.pre_ocr_data ?? rowRecord.preOcrData);
  const vendorOcr = asRecord(rowRecord.vendor_ocr_data ?? rowRecord.vendorOcrData);
  const documentType =
    row.document_type ??
    readOcrString(ocr, 'document_type') ??
    readOcrString(vendorOcr, 'document_type') ??
    readOcrString(preOcr, 'document_type');
  const province =
    row.issuing_province ??
    readOcrString(ocr, 'issuing_province') ??
    readOcrString(ocr, 'state') ??
    readOcrString(vendorOcr, 'issuing_province') ??
    readOcrString(vendorOcr, 'state') ??
    readOcrString(preOcr, 'issuing_province') ??
    readOcrString(preOcr, 'state');
  const country =
    row.issuing_country ??
    readOcrString(ocr, 'issuing_country') ??
    readOcrString(vendorOcr, 'issuing_country') ??
    readOcrString(preOcr, 'issuing_country');
  const parts = [documentType, province, country].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function formatCheckedAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

/** Human date for OCR / session expiry_date (date-only or ISO). */
export function formatDocumentExpiryDate(raw: string | null | undefined): string | null {
  if (!raw || !String(raw).trim()) return null;
  const value = String(raw).trim();
  // Prefer calendar date so timezone shifts do not move a date-only OCR value.
  const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    const dt = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, {
        dateStyle: 'medium',
        timeZone: 'UTC',
      });
    }
  }
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return value;
    return dt.toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return value;
  }
}

/** Plain-language expired-document body; includes OCR expiry when present. */
export function documentExpiredSummaryBody(
  row: Pick<IdentityLookupRow, 'expiry_date'>,
  fallbackMessage?: string | null,
): string {
  const expiry = formatDocumentExpiryDate(row.expiry_date);
  if (expiry) {
    return `The ID front shows an expiry date of ${expiry}, which is in the past.`;
  }
  return (
    (fallbackMessage && fallbackMessage.trim()) ||
    'The provided identity document was expired.'
  );
}

type AttemptFailureEvidence = {
  reason: string | null;
  subreason: string | null;
  message: string | null;
  checkpoint: string | null;
  detail: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function humanizeCode(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Structured evidence persisted on this exact session attempt. */
function attemptFailureEvidence(row: IdentityLookupRow): AttemptFailureEvidence {
  const checks = asRecord(row.checks_result);
  const verifications = Array.isArray(checks?.verifications) ? checks.verifications : [];
  const failed = verifications
    .map(asRecord)
    .find((item) => /FAIL|REJECT/i.test(String(item?.judgement ?? item?.status ?? '')));

  return {
    reason: nonEmptyString(checks?.failure_reason),
    subreason: nonEmptyString(checks?.subreason),
    message: nonEmptyString(checks?.failure_message),
    checkpoint: nonEmptyString(failed?.name ?? failed?.verificationType ?? failed?.check_key),
    detail: nonEmptyString(failed?.detail ?? failed?.reason),
  };
}

function buildFields(row: IdentityLookupRow, looksLike: string | null): IdentitySessionField[] {
  const fields: IdentitySessionField[] = [];
  const failure = attemptFailureEvidence(row);
  if (row.verification_attempt_number != null) {
    fields.push({ label: 'Attempt', value: String(row.verification_attempt_number) });
  }
  const doc = documentValue(row);
  if (doc) fields.push({ label: 'Document', value: doc });
  const expiry = formatDocumentExpiryDate(row.expiry_date);
  if (expiry) fields.push({ label: 'Expiry on ID', value: expiry });
  if (looksLike) fields.push({ label: 'Looks like', value: looksLike });
  if (failure.checkpoint) {
    fields.push({ label: 'Failed at', value: humanizeCode(failure.checkpoint) });
  }
  const failureCode = failure.subreason || failure.reason;
  if (failureCode) fields.push({ label: 'Failure code', value: failureCode });
  const checked = formatCheckedAt(row.verification_date || row.updated_at || row.created_at);
  if (checked) fields.push({ label: 'Checked', value: checked });
  return fields;
}

function fieldsToDetails(fields: IdentitySessionField[]): string[] {
  return fields.map((f) => `${f.label}: ${f.value}`);
}

function mitekFailed(row: IdentityLookupRow): boolean {
  return classifyMitekAuthenticity(row) === 'hard_fail';
}

function mitekNeedsReview(row: IdentityLookupRow): boolean {
  return classifyMitekAuthenticity(row) === 'review';
}

function finish(
  tone: IdentitySummaryTone,
  title: string,
  body: string,
  row: IdentityLookupRow,
  looksLike: string | null = null,
): IdentitySessionSummary {
  const fields = buildFields(row, looksLike);
  return { tone, title, body, fields, details: fieldsToDetails(fields) };
}

/**
 * Plain-language Identity summary for application-detail admins.
 * Prefer outcomes over raw session_status / vendor codes.
 */
function identitySessionStatus(row: IdentityLookupRow): string {
  const direct = String(row.session_status ?? '').trim();
  if (direct) return direct.toUpperCase();
  // Journey snapshot list items use `status`; tolerate that shape if passed through.
  const legacy = (row as { status?: unknown }).status;
  return typeof legacy === 'string' ? legacy.trim().toUpperCase() : '';
}

function isBlockedDocumentFailure(status: string, failure: AttemptFailureEvidence): boolean {
  return (
    /FAILED_BLOCKED_DOC/.test(status) ||
    /BLOCKED_DOCUMENT/.test(status) ||
    /BLOCKED_DOCUMENT/i.test(failure.reason ?? '') ||
    /BLOCKED_DOCUMENT/i.test(failure.subreason ?? '')
  );
}

export function resolveIdentitySessionSummary(row: IdentityLookupRow): IdentitySessionSummary {
  const status = identitySessionStatus(row);
  const failure = attemptFailureEvidence(row);
  const dedup = row.latestDedup;
  const bio = row.biometricMatchComparison ?? null;
  const hitName =
    customerDisplayName(dedup?.matched_hit_customer) ||
    (typeof dedup?.candidate_match?.ccid === 'string'
      ? String(dedup.candidate_match.ccid)
      : null);

  // Returning 1:1 selfie path
  if (bio?.kind === 'RETURNING_SELFIE_1TO1') {
    if (bio.passed === true || status === 'SUCCESS' || status === 'COMPLETED' || status === 'IDV_COMPLETED') {
      return finish(
        'success',
        'Passed — after sign-in',
        'Customer signed in and the selfie matched the account on file.',
        row,
      );
    }
    if (bio.passed === false) {
      return finish(
        'error',
        'Failed — selfie did not match',
        'Returning login expected, but the selfie did not match the customer on file.',
        row,
      );
    }
  }

  // Link-pending: existing customer found, OTP/sign-in required
  if (status === 'PENDING_EXISTING_ACCOUNT_LINK' || status === 'LINKED_PENDING_AUTH') {
    return finish(
      'warning',
      'Existing customer found',
      dedup?.message?.trim() ||
        'This submission matched a customer already on file. Sign-in was required before continuing.',
      row,
      hitName,
    );
  }

  // Duplicate / challenge style outcomes — customer may already exist
  if (dedup?.isDuplicate === true || status === 'REJECTED_DUPLICATE') {
    if (status === 'REJECTED_DUPLICATE' || status === 'REJECTED' || status === 'FAILED') {
      return finish(
        'error',
        'Failed — already in our system',
        dedup?.message?.trim() ||
          'This person already exists in our system and was blocked.',
        row,
        hitName,
      );
    }
    return finish(
      'warning',
      'Existing customer found',
      dedup?.message?.trim() || 'This person matched a customer already on file.',
      row,
      hitName,
    );
  }

  // Early and terminal failures may happen before Mitek or dedup, so checks_result
  // is the primary evidence. Keep this attempt-specific story even if a later
  // re-upload succeeds.
  const blockedDocument = isBlockedDocumentFailure(status, failure);
  const earlyFailBody =
    failure.message ||
    failure.detail ||
    (failure.subreason ? humanizeCode(failure.subreason) : null) ||
    (failure.reason ? humanizeCode(failure.reason) : null);

  if (status === 'FAILED_PREVERIFY_OCR') {
    return finish(
      'error',
      'Failed — required ID details were unreadable',
      earlyFailBody || 'Required information could not be read from the submitted ID image.',
      row,
    );
  }
  if (status === 'FAILED_IDV_ADAPTER') {
    return finish(
      'error',
      'Failed — verification provider could not complete',
      earlyFailBody || 'The identity verification provider could not complete this attempt.',
      row,
    );
  }
  if (status === 'FAILED_EXPIRED') {
    return finish(
      'error',
      'Failed — document expired',
      documentExpiredSummaryBody(row, earlyFailBody),
      row,
    );
  }
  // SDK uses FAILED_BLOCKED_DOC; classic path uses FAILED_BLOCKED_DOCUMENT;
  // checks_result may carry IV_BLOCKED_DOCUMENT even when status is terse.
  if (blockedDocument) {
    return finish(
      'error',
      'Failed — document not accepted',
      earlyFailBody || 'The submitted document type is not accepted for identity verification.',
      row,
    );
  }

  // Document authenticity — high-confidence fail vs low-confidence / undetermined review.
  if (mitekFailed(row)) {
    const copy = mitekAuthenticityOperatorCopy(row, 'hard_fail');
    return finish('error', copy.title, copy.body, row);
  }

  if (mitekNeedsReview(row)) {
    const copy = mitekAuthenticityOperatorCopy(row, 'review');
    return finish('warning', copy.title, copy.body, row);
  }

  if (/FAIL|REJECT/.test(status)) {
    return finish(
      'error',
      'Identity attempt failed',
      earlyFailBody || 'This identity verification attempt did not complete successfully.',
      row,
    );
  }

  // Clean pass
  if (
    status === 'SUCCESS' ||
    status === 'COMPLETED' ||
    status === 'IDV_COMPLETED' ||
    (dedup?.isDuplicate === false && row.mitekCalled && row.dossier?.findings?.authenticated === true)
  ) {
    if (!identitySessionHasPersistedEvidence(row)) {
      return finish(
        'warning',
        'Passed — details missing',
        'Identity checks cleared, but document details are not available for review. Confirm evidence below or request re-verification.',
        row,
      );
    }
    return finish(
      'success',
      'Passed',
      'Looks new to our system, and the ID supports who they claim to be.',
      row,
    );
  }

  if (status === 'PENDING' || status === 'IN_PROGRESS' || status === 'PROCESSING') {
    return finish(
      'warning',
      'In progress',
      'Identity verification has started but is not finished yet.',
      row,
    );
  }

  // Customer onboarded with dedup cleared — still require document_type for a green pass.
  if (row.customer && dedup?.isDuplicate === false) {
    if (!identitySessionHasPersistedEvidence(row)) {
      return finish(
        'warning',
        'Passed — details missing',
        'Identity checks cleared, but document details are not available for review. Confirm evidence below or request re-verification.',
        row,
      );
    }
    return finish(
      'success',
      'Passed',
      'Looks new to our system, and identity checks cleared.',
      row,
    );
  }

  return finish(
    'info',
    'Identity session recorded',
    'Review photos and the new-customer / document evidence below for this attempt.',
    row,
  );
}
